import { Component, OnInit, HostListener, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from 'src/app/services/api.service';
import { SimrUnsavedChangesService } from 'src/app/services/simr-unsaved-changes.service';
import { clearSimrPostRoleRedirect } from 'src/app/utils/simr-role-selection';

@Component({
  selector: 'app-switch-account',
  templateUrl: './switch-account.component.html',
  styleUrls: ['./switch-account.component.scss'],
})
export class SwitchAccountComponent implements OnInit, OnDestroy {
  currentUser: any = null;
  currentRole: any = null;
  linkedAccounts: any[] = [];
  loadingAccounts = false;
  switching = false;
  private userSub: any;

  showDropdown = false;
  showAccountList = false;
  private hoverCloseTimer: any = null;

  constructor(
    public apiService: ApiService,
    private router: Router,
    private simrUnsavedChanges: SimrUnsavedChangesService,
  ) {}

  ngOnInit() {
    this.loadCurrentUser();
    this.userSub = this.apiService.currentUser$.subscribe(user => {
      if (user) {
        this.currentUser = user;
        this.currentRole = user.role;
        this.fetchLinkedAccounts();
      }
    });
    this.fetchLinkedAccounts(); // Fetch immediately on init
  }

  ngOnDestroy() {
    if (this.userSub) {
      this.userSub.unsubscribe();
    }
  }

  loadCurrentUser() {
    const u = localStorage.getItem('current_user');
    const r = localStorage.getItem('role_detail');

    if (u) {
      this.currentUser = JSON.parse(u);
      console.log('[SwitchAccount] currentUser', this.currentUser);
    }

    if (r) {
      this.currentRole = JSON.parse(r);
      console.log('[SwitchAccount] currentRole', this.currentRole);
    }
  }

  getUserName(u: any) {
    if (!u) return '';
    return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || '—';
  }

  isPrimaryAccount(acc: any): boolean {
    return acc?.is_parent === true || acc?.is_parent === 'true' || acc?.is_parent === 1;
  }

  async toggleDropdown() {
    this.clearHoverCloseTimer();
    this.showDropdown = !this.showDropdown;
    this.showAccountList = false;

    if (this.showDropdown) {
      this.loadCurrentUser(); // Refresh from localStorage
      await this.fetchLinkedAccounts();
    }
  }

  async onMouseEnterContainer() {
    // Desktop UX: open switcher on hover
    if (window.innerWidth <= 991) return;
    this.clearHoverCloseTimer();
    if (!this.showDropdown) {
      this.showDropdown = true;
      this.showAccountList = false;
      this.loadCurrentUser();
      await this.fetchLinkedAccounts();
    }
  }

  onMouseLeaveContainer() {
    // Small delay avoids flicker when moving pointer within panel
    if (window.innerWidth <= 991) return;
    this.clearHoverCloseTimer();
    this.hoverCloseTimer = setTimeout(() => {
      this.showDropdown = false;
      this.showAccountList = false;
    }, 160);
  }

  private clearHoverCloseTimer() {
    if (this.hoverCloseTimer) {
      clearTimeout(this.hoverCloseTimer);
      this.hoverCloseTimer = null;
    }
  }

  async showSwitchAccount() {
    this.showAccountList = true;

    if (this.linkedAccounts.length === 0) {
      await this.fetchLinkedAccounts();
    }
  }

  backToMenu() {
    this.showAccountList = false;
  }

  async fetchLinkedAccounts() {
    this.loadingAccounts = true;
    console.log('[SwitchAccount] fetchLinkedAccounts');
  
    try {
      const accounts = await this.apiService.getLinkedAccounts();
  
      this.linkedAccounts = (accounts || []).filter((acc: any) => {
        return (acc?.status || '').toLowerCase() === 'active';
      });
  
      if (this.linkedAccounts.length > 0) {
        this.linkedAccounts.sort((a, b) => {
          const aPrimary = this.isPrimaryAccount(a) ? 0 : 1;
          const bPrimary = this.isPrimaryAccount(b) ? 0 : 1;
          if (aPrimary !== bPrimary) return aPrimary - bPrimary;

          if (a.date_created && b.date_created) {
            return new Date(a.date_created).getTime() - new Date(b.date_created).getTime();
          }
  
          const emailA = a.email || '';
          const emailB = b.email || '';
  
          if (emailA && emailB) {
            return emailA.localeCompare(emailB);
          }
  
          if (a.id && b.id) {
            return a.id > b.id ? 1 : -1;
          }
  
          return 0;
        });
  
        const roleCounters: { [key: string]: number } = {};
        const roleOccurrences: { [key: string]: number } = {};
  
        this.linkedAccounts.forEach(acc => {
          const roleName = acc.role?.name || acc.role_name || 'User';
          roleOccurrences[roleName] = (roleOccurrences[roleName] || 0) + 1;
        });
  
        this.linkedAccounts.forEach(acc => {
          const roleName = acc.role?.name || acc.role_name || 'User';
          if (roleOccurrences[roleName] > 1) {
            roleCounters[roleName] = (roleCounters[roleName] || 0) + 1;
            acc.display_role_name = `${roleName} - ${roleCounters[roleName]}`;
          } else {
            acc.display_role_name = roleName;
          }
        });
      }
  
      console.log('[SwitchAccount] linkedAccounts', this.linkedAccounts || []);
    } catch (e) {
      console.error('[SwitchAccount] fetchLinkedAccounts error', e);
      this.linkedAccounts = [];
    } finally {
      this.loadingAccounts = false;
    }
  }

  isCurrentAccount(account: any): boolean {
    return this.currentUser?.id === account?.id;
  }

  async switchTo(account: any) {
    if (this.isCurrentAccount(account) || this.switching) {
      return;
    }

    if (this.simrUnsavedChanges.hasUnsavedChanges()) {
      console.log('[SwitchAccount] Unsaved changes on edit form — confirming before switch');
      const confirmed = await this.apiService.presentAlertConfirm(
        'Unsaved Changes',
        'You have unsaved changes. Switching accounts will refresh the page and you may lose your progress. Continue?',
        'Yes, Switch',
        'Stay Here'
      );
      if (confirmed) {
        // user clicked 'Stay Here' (resolve(true))
        return;
      }
    }

    this.switching = true;
    console.log('[SwitchAccount] switching to', {
      currentUserId: this.currentUser?.id,
      targetUserId: account?.id,
      targetRole: account?.display_role_name || account?.role?.name || account?.role_name
    });

    try {
      await this.apiService.switchAccount(account.id);
      const updatedUser = JSON.parse(localStorage.getItem('current_user') || 'null');
      console.log('[SwitchAccount] switch completed', {
        requestedUserId: account?.id,
        resolvedUserId: updatedUser?.id,
        resolvedRole: updatedUser?.role?.name || updatedUser?.role
      });
      this.apiService.present_toast('success', 'Switched to ' + (account.role?.name || 'Account'));
      this.showDropdown = false;

      // Manual role switch must not restore a deep link saved from a previous screen (e.g. DDEX).
      clearSimrPostRoleRedirect();

      // Force reload to apply new role permissions and configuration
      setTimeout(() => {
        window.location.href = '/products';
      }, 500);
    } catch (e) {
      console.error('[SwitchAccount] switchTo error', e);
      this.apiService.present_toast('error', 'Failed to switch account');
      this.switching = false;
    }
  }

  async logout() {
    const confirmed = await this.apiService.presentAlertConfirm(
      'Sign Out',
      'Are you sure you want to sign out?',
      'Sign Out',
      'Cancel'
    );
    
    // Note: presentAlertConfirm resolves with false for the first button (OK/Sign Out) 
    // and true for the second button (Cancel).
    if (!confirmed) {
      this.apiService.logout();
      this.showDropdown = false;
    }
  }


  @HostListener('document:click', ['$event'])
  onDocClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.sa-container')) {
      this.clearHoverCloseTimer();
      this.showDropdown = false;
      this.showAccountList = false;
    }
  }
}